import { applyEncimeraThicknessMm, isEncimeraLinkedSonItem, isFreeDimensionLateralCascadaAnchoCatalog } from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'

const INHERITED_FIELDS = [
  'name',
  'url',
  'id',
  'searchID',
  'groupID',
  'acabado',
  'description',
  'descripcion',
  'color',
  'roughness',
  'metalness',
  'envMapIntensity',
  'bumpScale',
  'opacity',
  'mirror',
  'img',
] as const

type FinishTexture = {
  zone?: string
  zoneName?: string
  zoneDisplayName?: string
  linkedTextureID?: number
  idAcabadoUnidadModeloAcabado?: string
  url?: string
  img?: string
  name?: string
  [key: string]: unknown
}

type FinishItem = {
  type?: string
  description?: string
  catalogID?: string | number
  codigo?: string | number
  typeOfSon?: string | null
  textures?: FinishTexture[]
  zones?: Record<string, { material?: Record<string, unknown> }>
  image?: unknown
}

export function isEncimeraFinishTexture(texture?: FinishTexture | null): boolean {
  return texture?.zoneDisplayName === 'Encimera' || texture?.zone === 'Encimera'
}

export function isCascadaLateralItem(item?: FinishItem | null): boolean {
  if (!item || item.type !== 'linkedItem') return false
  if (isEncimeraLinkedSonItem(item)) return false
  if (isFreeDimensionLateralCascadaAnchoCatalog(item)) return true
  return String(item.description ?? '').toLowerCase().includes('cascada')
}

export function getItemEncimeraFinishTexture(item?: FinishItem | null): FinishTexture | undefined {
  if (!item?.textures?.length) return undefined
  return item.textures.find(isEncimeraFinishTexture) ?? item.textures[0]
}

export function mergeEncimeraFinish<T extends Record<string, unknown>>(
  prev: T,
  src: FinishTexture,
): T {
  const merged = { ...prev }
  for (const key of INHERITED_FIELDS) {
    if (src[key] !== undefined) merged[key] = src[key]
  }
  return merged
}

export function applySharedEncimeraFinishToItem(
  item: FinishItem,
  src: FinishTexture,
  options?: { applyThickness?: boolean },
): boolean {
  if (!item.textures?.length || !src) return false

  const hasEncimeraZone = item.textures.some(isEncimeraFinishTexture)
  item.textures = item.textures.map((texture) => {
    if (hasEncimeraZone && !isEncimeraFinishTexture(texture)) return texture
    const merged = mergeEncimeraFinish(texture, src)
    merged.zoneName = texture.zoneName
    merged.zoneDisplayName = texture.zoneDisplayName
    merged.zone = texture.zone
    merged.linkedTextureID = texture.linkedTextureID
    merged.idAcabadoUnidadModeloAcabado = texture.idAcabadoUnidadModeloAcabado
    return merged
  })

  const zones = item.zones
  if (zones) {
    const nextZones = { ...zones }
    for (const [zoneName, zone] of Object.entries(zones)) {
      const zoneIsEncimera =
        isEncimeraFinishTexture(zone?.material as FinishTexture | undefined) ||
        /encimera/i.test(zoneName)
      if (hasEncimeraZone && !zoneIsEncimera) continue
      nextZones[zoneName] = {
        ...zone,
        material: mergeEncimeraFinish(zone?.material ?? {}, src),
      }
    }
    item.zones = nextZones
  }

  if (src.img || src.url) item.image = src.img || src.url
  if (options?.applyThickness && isEncimeraLinkedSonItem(item)) {
    applyEncimeraThicknessMm(item as Parameters<typeof applyEncimeraThicknessMm>[0])
  }
  return true
}

export function sharesEncimeraCascadaFinish(item?: FinishItem | null): boolean {
  return Boolean(item && (isEncimeraLinkedSonItem(item) || isCascadaLateralItem(item)))
}
